/**
 * Subtask Type Constants
 * Use these constants instead of string literals throughout the codebase
 */
export const SUBTASK_TYPE = {
  VIDEO: "video",
  NOTES: "notes",
  MCQ: "mcq",
  CODING: "coding",
  PROJECT_CODING: "project_coding"
} as const;

export type SubtaskTypeValue = typeof SUBTASK_TYPE[keyof typeof SUBTASK_TYPE];

export interface Phase {
  id: string;
  name: string;
  parts: Part[];
}

export interface Part {
  id: string;
  name: string;
  tasks: Task[];
}

export interface Task {
  id: string;
  name: string;
  subtasks: Subtask[];
}

export interface VideoContent {
  content_id: number;
  playback_info?: string;
  otp?: string;
  text: string;
  time: string;
  level: string;
}

export interface NotesContent {
  content_id: number;
  content: string;
  text: string;
  time: string;
  level: string;
}

export interface SubtopicContent {
  videos?: { [key: string]: VideoContent };
  files?: { [key: string]: NotesContent };
}

export interface ContentItem {
  type: SubtaskTypeValue;
  count: {
    level1: string[];
    level2: string[];
    level3: string[];
  };
  content_id?: string;
}

export interface SubtaskContentData {
  content: {
    level1: Array<{ [key: string]: string }>;
    level2: Array<{ [key: string]: string }>;
    level3: Array<{ [key: string]: string }>;
  };
  videoId?: string;
  time?: string;
  id?: string;
}

export interface SubtaskContent {
  [taskId: string]: {
    subtopic_id: string;
    content: ContentItem[];
  };
}

export interface Subtask {
  subtask_id: string;
  id?: string; // Question/content ID (for VIDEO/NOTES/PROJECT_CODING) - loaded from JSON's "id" field
  name: string;
  isMandatory: boolean;
  showDropdowns: boolean;
  actionType?: "select" | "assign";
  subjectId: string;
  topicId: string;
  subtopicId: string;
  subtaskType: SubtaskTypeValue;
  level: string;
  contentLevel?: string;
  contentCounts?: {
    level1: string;
    level2: string;
    level3: string;
  };
  createdContent?: ContentItem[];
  selectedContent?: SubtaskContentData;
  availableContent?: SubtopicContent;
  selectedContentLevel?: string;
  selectedContentId?: string;
  selectedContentTime?: string;
  editor?: string | null;
}

export interface Subject {
  subject_id: string;
  subject_name: string;
}

export interface Subtopic {
  subtopic_id: string;
  subtopic_name: string;
}

export interface Topic {
  topic_id: string;
  topic_name: string;
  sub_topics?: Subtopic[];
}

export interface DeleteTarget {
  type: "phase" | "part" | "task" | "subtask";
  id: string;
  name: string;
  phaseIndex: number;
  partIndex?: number;
  taskIndex?: number;
  subtaskIndex?: number;
}

